/**
 * Single source of truth for tunables. See CLAUDE.md architecture rule 5.
 * Values may be hot-swapped from the debug overlay in later phases.
 */

export const CONFIG = {
  /** Fixed logic tick rate (Hz). Deterministic; independent of display FPS. */
  tickHz: 60,

  /** Max sim ticks processed per frame — spiral-of-death guard. */
  maxTicksPerFrame: 5,

  /** Tile grid rendering. */
  render: {
    /** On-screen tile size in device-independent pixels. */
    tileSize: 20,
    /** Glyph atlas source font pixel size. 80 = 4× tileSize gives plenty
     *  of source detail so even after DPR × downscale to on-screen the
     *  glyphs still read crisp. */
    atlasFontPx: 80,
    /** Font-family stack used to bake the atlas. */
    atlasFontFamily:
      'ui-monospace, "Cascadia Mono", "Consolas", "DejaVu Sans Mono", "Menlo", monospace',
    /** Background color of the world. */
    bgColor: 0x0b0b10,
    /** Screen-up offset (px) of the torso tile above the legs tile, giving
     *  the player its two-tile-tall look. Collapsed to 0 while crouching so
     *  the torso renders within the legs' tile. ≈ tileSize = one full tile. */
    playerTorsoOffsetPx: 20,
    /** Peak screen-up lift (px) of the whole body during a sprint vault — an
     *  arc that crests as the player clears the cover, for a "jump" feel. */
    vaultHopPx: 16,
  },

  /** Movement (tiles/sec, and 1 tile = 1 m). The player moves as a free
   *  floating-point position (see movement.ts), so velocity here is applied
   *  continuously — direction changes are instant, which reads as snappier
   *  than the old tile-committed stepping even at the same nominal speed.
   *  Tuned toward *dynamic* feel: brisk walk, quick jog, fast sprint. */
  speed: {
    run: 11,
    walk: 6,
    crouch: 3.5,
  },

  /** Free-fly "spectator" observer after the player dies: drifts anywhere on
   *  the map (through walls), with 0 vision of its own but full map reveal. */
  spectator: {
    /** Fly speed in tiles/sec — a touch faster than a run so you can roam. */
    speed: 13,
  },

  /** Two-part movement model (legs = WASD, torso = arrows). */
  move: {
    /** Seconds the player must be standing still (no move key) before the
     *  free float eases to the nearest tile centre. A brief beat so a quick
     *  stop-and-go doesn't yank you onto the grid. */
    snapDelaySec: 0.15,
    /** Per-second exponential approach rate toward the tile centre once the
     *  snap delay has elapsed. Higher = snappier settle. */
    snapRate: 22,
    /** Distance (tiles) within which the ease locks exactly onto the tile. */
    snapEpsilon: 0.02,
  },

  /** Camera behaviour. The world rotates so the torso's look direction
   *  ("up") tracks where the player is aiming. Heading is smoothed by the
   *  renderer's heading lerp, which supplies the swing when the torso turns. */
  camera: {},

  /** Stamina model — drain/regen per second, plus recovery hysteresis. */
  stamina: {
    max: 100,
    drainPerSec: 25,
    regenPerSec: 15,
    /** Fraction of max the player must recover to before run re-enables after depletion. */
    recoverThreshold: 0.3,
    /** One-off stamina cost of a sprint vault — a burst of effort on top of the
     *  running drain. */
    vaultCost: 15,
  },

  /** Sound flood-fill costs (per tile entered) and event loudness
   *  (tile-units). Cost = how much loudness budget is spent crossing INTO a
   *  tile of that type, so higher = more dampening. See CLAUDE.md > Sound and
   *  `sim/sound.ts`. The same flood drives both guard hearing and the visual
   *  sound-field overlay, so tuning here changes both at once. */
  sound: {
    cost: {
      /** Open floor: base cost, 1 per tile of distance. */
      floor: 1,
      /** Wall (█): near-total dampening. Set high enough that ordinary
       *  sounds cannot cross at all and only a very loud gunshot leaks
       *  ~1–2 tiles past it. The room-fill effect comes from reflections
       *  (see `addReflections`), not from sound tunnelling through walls. */
      wall: 20,
      /** Cubicle partition (═║╬): thin office panels — only LIGHT sound
       *  dampening, less than a half wall (sound carries through cubicles
       *  nearly like open floor). */
      cubicle: 1.5,
      /** Closed door (+): dampens like a partition. (No open-door tile in
       *  the current map; if one is added it should use `floor`.) */
      door: 4,
      /** Half-wall / low cover (▄): mild attenuation, like light cover
       *  scaling vision occlusion. */
      halfWall: 2,
    },
    /**
     * Multiplicative TRANSMISSION when sound crosses into a tile — what
     * fraction of the remaining loudness gets through. Cost (above) models the
     * distance sound gives up crossing a space; transmission models a barrier
     * it has to pass THROUGH, which is a different thing and can't be expressed
     * as a distance penalty: a shout and a whisper both lose 90% at a door.
     */
    transmission: {
      /** A shut door leaks a tenth of what hits it. */
      doorClosed: 0.1,
      /** A wall's muffling is mostly its COST (20 — ordinary sound can't pay
       *  it). This trims what survives on top, without breaking the rule
       *  CLAUDE.md sets: a gunshot still bleeds a tile or two through. */
      wall: 0.6,
      /** Thin office panels muffle a little on the way through. */
      cubicle: 0.75,
    },
    loudness: {
      footstepRun: 9,
      /** Normal walking on a hard floor is fairly audible — only sneaking
       *  (crouch) is quiet. Bumped up for realism (was 5). */
      footstepWalk: 7,
      footstepCrouch: 2,
      silencedPistol: 6,
      knifeTakedown: 2,
      smgShot: 25,
      /** Scrambling over a half wall — a noticeable clatter that draws nearby
       *  guards (louder than a run footstep). */
      vault: 12,
      /** Bare-fisted attack. Louder than the knife by design (a knife takedown
       *  is precise; a haymaker is not). */
      punch: 4,
    },
    /** How long a sound event stays in the queue (ticks) — governs the
     *  audible-entity reveal window (`player.hearingUntil`), not the visual
     *  overlay. */
    fadeAgeTicks: 120,
    /** Awareness bump per unit of received loudness. */
    awarenessBumpPerLoudness: 3,
    /** Sound-field OVERLAY persistence (ticks). The region is computed
     *  instantly (no expanding ring) and shown in full the moment a sound is
     *  emitted; this is only how long a one-shot sound lingers before it
     *  fades out, so a single footstep doesn't strobe for one frame. Kept
     *  short so the overlay still reads as snappy/instant. At 60 Hz, 18 ≈
     *  0.3 s. Loud events (gunshots) scale this up — see `soundField.ts`. */
    vizPersistTicks: 18,
  },

  /** Vision cone. Player sight reaches ~34 tiles so the workhall reads
   *  as a room, not a keyhole. Guards use the shorter guardRange so
   *  the player still has the range advantage stealth benefits from. */
  vision: {
    range: 34,
    /** Guards see as far as the player (same cone). */
    guardRange: 34,
    /** Half-angle in radians (60° each side → 120° cone). */
    halfAngleRad: Math.PI / 3,
    /** Multipliers applied to range + halfAngle when the observer is crouched. */
    crouchRangeMult: 0.75,
    crouchAngleMult: 0.9,
    /** Close-proximity "6th sense" radius (Chebyshev tiles). Within it an entity
     *  is sensed + attackable even in pitch darkness (no light / cone needed). */
    proximityTiles: 2,
  },

  /** Awareness meter thresholds and rates. */
  awareness: {
    max: 100,
    thresholds: { suspicious: 30, cautious: 60, alert: 90 },
    /** Base rise per second when player is fully exposed at close range. */
    baseRisePerSec: 40,
    /** Decay per second when no stimulus. */
    decayPerSec: 6,
    /** Stance multipliers on the awareness rise rate. A crouched player in
     *  plain sight is spotted just as fast as a walking one (crouch = 1.0) —
     *  crouch's stealth benefit is CONCEALMENT behind cover, not slower
     *  detection when you're actually visible. Running is louder to the eye. */
    stanceMult: { run: 1.4, walk: 1.0, crouch: 1.0 },
    /** Chebyshev-tile radius for the "guard section" that receives a
     *  cautious-state broadcast (CLAUDE.md > Awareness > Cautious). */
    broadcastRadius: 20,
    /** Sniper-Elite "seen" ghost: when a guard spots you, a player-visible ghost
     *  drops at that tile and the guard commits to revisiting it (instead of
     *  chasing your live position) for this many seconds. */
    sightingGhostSec: 5,
  },

  /** Blood-trail tracking (sim/bloodTrail.ts). Unaware/suspicious guards that
   *  SEE blood follow it — along the freshness gradient — to whoever's bleeding
   *  at the end, then engage the player or patch up a wounded guard. */
  blood: {
    /** Minimum pooled blood on a tile for a guard to first NOTICE it (in view). */
    noticeThreshold: 20,
    /** Minimum pooled blood for a tile to count as part of a followable trail. */
    minTrail: 6,
    /** Chebyshev tiles: how close the tracker must get to the bleeder at the
     *  trail's end to "find" them (engage player / heal guard). */
    findReachTiles: 1,
    /** Health packs each guard spawns carrying (spent to heal a wounded guard). */
    healthPacksPerGuard: 1,
    /** HP a single health pack restores to every body part. */
    healthPackHpPerLimb: 40,
    /** Ticks a guard ignores blood after a track ends (stops instant re-notice). */
    trackCooldownTicks: 180,
    /** Ticks a guard will spend trying to reach one trail tile before giving up
     *  (unreachable / boxed-in trail leg). */
    legTimeoutTicks: 240,
  },

  /** Pathfinding tunables. */
  pathing: {
    /** Extra A* cost per tile that has no cover between it and the player
     *  when an alert guard is repositioning. Small so guards still route
     *  reasonably; large enough that they'll detour through a cubicle if
     *  the extra distance is short. */
    openTilePenaltyAlert: 1.2,
  },

  /** HUD tunables. */
  hud: {
    /** Seconds the directional "F Focus!" prompt shows after you're hit. */
    focusPromptSec: 2.6,
  },

  /** Janitor (sim/janitor.ts): cleans blood when a guard calls it after the
   *  threat's been dealt with, and runs to warn a guard if it sees an intruder. */
  janitor: {
    /** Seconds spent mopping a single bloody tile before it's wiped clean. */
    mopSecPerTile: 0.8,
    /** Minimum pooled blood on a tile for the janitor to bother cleaning it. */
    cleanThreshold: 6,
    /** Ticks the janitor will try to reach one blood tile before giving up. */
    reachTimeoutTicks: 600,
    /** Chebyshev range at which the janitor spots an intruder (lit + LOS). It
     *  then runs to the nearest guard it knows to warn them. */
    sightRange: 9,
    /** Janitor-role headcount quota per floor — you can't have more than this
     *  many janitors alive at once. */
    quota: 1,
  },

  /** Guard tactics. */
  guard: {
    /** Duck & peek cover rhythm: while an alert guard has cover between itself
     *  and the player, it crouches (hidden, can't see/fire) for `duckSec`, then
     *  stands to shoot for `peekSec`, and repeats. */
    cover: {
      duckSec: 1.0,
      peekSec: 0.9,
    },
    /** Radians/sec of the flashlight sweep: while patrolling in the dark with a
     *  torch, a guard oscillates its look direction (cone + beam) ±45° around its
     *  travel direction to scan more as it walks carefully. */
    flashlightSweepRate: 2.4,

    /** Section-based room patrol (sim/patrol.ts + sim/guard.ts patrolSection).
     *  A room interior is partitioned into ~sectionW×sectionH cells; one guard
     *  per section scans then roams it. Section count adapts to room size and
     *  live guard count. */
    /** Relief breaks. Nobody is POSTED to a restroom (see patrol.ts
     *  NEVER_POSTED) — a guard goes when they need to, which makes a bathroom a
     *  place that is usually empty and occasionally very much not. */
    relief: {
      /** Average seconds on duty between breaks. */
      everySec: 240,
      /** Random spread either side of that, so a floor's guards don't sync up. */
      jitterSec: 120,
      /** Seconds spent there before returning to post. */
      dwellSec: 20,
    },

    /** Manned security stations (map.stations, guard.ts manStation). */
    station: {
      /** Tiles either side of the desk a stationed guard sweeps when it has no
       *  section rect to work from. */
      watchRadius: 6,
    },
    patrol: {
      /** Target section footprint (tiles). ~25×25 ⇒ one guard per ~625 tiles —
       *  large regions by design (most test-map rooms end up a single section). */
      /** Target footprint one guard is responsible for. Floors got a lot bigger,
       *  so a section did too: coverage (WHICH rooms are watched) is the budget's
       *  job, while this decides how thinly one guard is spread inside a room. */
      sectionW: 34,
      sectionH: 34,
      /** Cap on the area-derived guard count per room (keeps a big room from
       *  spawning a swarm). */
      maxSectionsPerRoom: 8,
      /** A floor needs at least this many posted rooms before anyone is spared
       *  to roam it as captain. A one-guard building doesn't have a supervisor
       *  — it has a guard. */
      captainMinRooms: 3,
      /** Chebyshev tiles around the player's way in where no guard STARTS. The
       *  entrance room is noPatrol already, but the corridor runs right past it,
       *  so without this a guard is staring at you on frame one. */
      entranceClearance: 8,
      /** Ticks a guard holds each cardinal while sweeping a new section (~0.4s). */
      scanHoldTicks: 24,
      /** Fraction of a section's visible floor that must enter the cone to
       *  finish the scan (<1 so occluded tiles can't block completion). */
      scanCoverageFrac: 0.7,
      /** Hard cap (ticks) on a single scan so it can never hang (~5s). */
      scanTimeoutTicks: 300,
      /** Ticks a guard pauses at each roam point inside its section (~1.5s). */
      roamDwellTicks: 90,
      /** Max tiles a roam leg wanders from the guard (clamped to the section).
       *  Larger so a guard ranges actively across the big 25×50 regions. */
      roamRadius: 12,
      /** Phase rate (rad/sec) of the active patrol sweep: while patrolling its
       *  region an unaware guard oscillates its cone ±45° around its travel
       *  direction so it actively sweeps the area rather than only staring
       *  where it walks. See `sweepPatrolFacing` in guard.ts. */
      sweepRate: 1.8,
    },

    /** Occasional distraction (breaks patrol monotony). While on routine patrol
     *  (unaware + fully calm) a guard now and then "zones out" for a couple of
     *  seconds — its sight range collapses to `rangeMult` of normal, opening a
     *  brief window — then snaps back. The captain never zones out.
     *  See the distraction pass in `updateGuardAwareness`. */
    distract: {
      /** Guard vision range while distracted, as a fraction of the normal range. */
      rangeMult: 0.18,
      /** Base distraction duration (ticks) + up to `durationJitterTicks` extra. */
      durationTicks: 120, // ~2s
      durationJitterTicks: 90, // up to +1.5s
      /** Minimum ticks between distractions + up to `intervalJitterTicks` extra. */
      minIntervalTicks: 720, // ~12s
      intervalJitterTicks: 1080, // up to +18s
    },

    /** Floor captain (sim/dispatch.ts): one dedicated guard per floor that
     *  patrols through every patrol region (section) to check on it, and
     *  re-partitions a room's coverage when its roster changes. */
    captain: {
      /** Ticks the captain lingers at each region before moving on (~2s). */
      dwellTicks: 120,
    },

    /** Door watch (sim/doorWatch.ts). A stationed guard (or the janitor) that has
     *  personally SEEN a door closed remembers it; if it later spots that same
     *  door left open within `radius` tiles, it walks over and shuts it. A door
     *  first met already open never triggers — and the roaming captain, which
     *  OPENS doors as it transits, is exempt from closing them. */
    doorWatch: {
      /** Chebyshev range within which a watcher cares about a door it knows. */
      radius: 10,
      /** Cooldown (ticks) after shutting one door before it'll chase another, so
       *  it doesn't ping-pong on a door another NPC keeps reopening (~3s). */
      cooldownTicks: 180,
      /** Loudness emitted when a door is pulled shut. */
      closeSound: 4,
    },
  },

  /** Flash-blinding (sim/blind.ts). A bright flash — an adjacent muzzle flash,
   *  NV goggles overwhelmed by bright light, or a future flashbang — dazes an
   *  entity: it can't move for `stunSec`, and its vision whites out (sees its
   *  whole cone as overexposed ~max-plus light) for `durationSec`, eroding back
   *  to normal. Applies to the player and every NPC. */
  blind: {
    /** Total blind duration (seconds) — the whiteout erodes over this. */
    durationSec: 5,
    /** Can't move for this long at the flash (the dizzy stun). */
    stunSec: 1,
    /** A muzzle flash blinds live entities within this Chebyshev radius of the
     *  muzzle tile (the shooter excepted). 1 = adjacent tiles. */
    muzzleRadius: 1,
  },

  /** Lighting. Per-tile light level runs 0..`max`. Fixtures flood light out to
   *  `fixtureRadius`, blocked by full walls (they pass over low cover). Detection
   *  scales with the light on the player's tile: below `hideBelow` you're unseen
   *  (sneak in the dark), ramping to full detection at `fullAt`. */
  light: {
    /** Light scale ceiling — a tile in direct light. */
    max: 10,
    /** Baseline light on a tile no fixture reaches. 0 = true darkness: unlit
     *  tiles render black and are invisible, and you're unseen there. */
    ambient: 0,
    /** Default ceiling-fixture brightness (at the fixture tile) + reach (tiles).
     *  Light falls off linearly to ~0 at the radius, so a LARGER radius is a
     *  gentler drop-off: fixtures cover more area and the dim mid-points between
     *  them on the fixture grid stay well above `fullAt` instead of sinking into
     *  the ~3 "barely lit" band. (Workhall fixtures are 11 tiles apart.) */
    fixtureIntensity: 10,
    fixtureRadius: 12,
    /** How many bounce passes the light does off walls, and the fraction of a
     *  tile's light that reflects into a shadowed neighbour each pass. Fills
     *  hard shadows so a lit room reads as lit while occluders still darken. */
    reflectPasses: 2,
    reflectFraction: 0.55,
    /** Detection light ramp: at/below `hideBelow` the player is effectively
     *  invisible to sight; at/above `fullAt` sight works normally; linear
     *  between. Sound is unaffected by light. */
    hideBelow: 2.5,
    fullAt: 6,
    /** Render brightness floor for the WORLD (unlit tiles → black). The player
     *  avatar keeps a separate higher floor so you can always see yourself. */
    renderFloor: 0,
    /** At/below this light level a tile is treated as UNLIT: entities there
     *  aren't rendered, can't be focused, and cast no visible vision cone. */
    darkEps: 0.5,
    /** Night vision adds this to a viewer's perceived light level (see
     *  `perceivedLight`). At +10 a pitch-black tile reads as fully lit; a
     *  base-`max` tile reads as `max`+10 = 20 and renders pure white
     *  (overexposed — too bright to make out). */
    nvBoost: 10,
    /** Night-vision flash-blind: with goggles ON, a tile at/above `nvBlindAt`
     *  (actual light) overexposes them and BLINDS the wearer (see sim/blind.ts).
     *  A guard's goggles auto-activate when its tile light drops below
     *  `nvOnBelow`, so cutting power → goggles on → restoring power in a bright
     *  spot → blind. (Max actual light is ~`max`; nvBlindAt = "brightly lit".) */
    nvBlindAt: 6,
    nvOnBelow: 2.5,
    /** A gunshot briefly lights its surroundings (muzzle flash) — intensity at
     *  the muzzle, reach, and life in seconds. Reveals a shooter in the dark. */
    muzzleIntensity: 7,
    muzzleRadius: 4,
    muzzleLifeSec: 0.14,
    /** A guard restoring power flips ONE off-breaker every this-many seconds
     *  while stood at the fusebox (staged, not instant). */
    fuseFlipSec: 0.5,
    /** Guard flashlights (sim/light.ts computeFlashlights): a non-NV guard whose
     *  tile light is below `flashlightOnBelow` switches on a torch — a narrow
     *  cone (`flashlightWidthFrac` of the vision half-angle, so ~1/3–1/2 the cone
     *  width) reaching `flashlightRange` tiles with linear falloff from
     *  `flashlightIntensity` at the guard. It lights tiles for detection + render. */
    flashlightOnBelow: 4,
    flashlightWidthFrac: 0.4,
    flashlightRange: 12,
    flashlightIntensity: 7,
    /** Windows on the floor's end walls admit sunlight. Peak brightness at solar
     *  noon and reach in tiles; `sunlightIntensity` scales peak by time of day. */
    windowIntensity: 10,
    windowRadius: 13,
  },

  /** Weapon stats. Damage is per-hit, before limb resolution. */
  weapons: {
    smg: { damage: 8, rangeTiles: 14, fireIntervalSec: 0.1, magazine: 30 },
    silencedPistol: { damage: 14, rangeTiles: 12, fireIntervalSec: 0.35, magazine: 12 },
    knife: { damage: 20 },
    unarmed: { damage: 6, fireIntervalSec: 0.5 },
    /** Heavy blunt melee (pipe/sledge) — the only melee that can smash a lock. */
    heavyBlunt: { damage: 34, fireIntervalSec: 0.7 },
  },

  /** Locks (sim/lock.ts). A lock guards a door or container. It generates a
   *  STATIC pin/tumbler layout once at map build (seeded, for the deferred
   *  lockpicking minigame) and has HP = securityLevel × hpPerLevel so it can be
   *  shot off: at hpPerLevel≈a silenced-pistol round (14 dmg), a level-1 lock
   *  breaks in one hit and higher-security locks need several. */
  lock: {
    hpPerLevel: 14,
    /** Pin columns per security level (for the future pick/rake minigame). */
    pinsPerLevel: 2,
    maxSecurityLevel: 5,
    /** Base seed folded with each lock's tile coords for reproducible pins. */
    mapSeed: 1337,
    // -- pick/rake minigame (sim/lockpick.ts) --
    /** The RAKE only works when EVERY pin column is this short or shorter — a
     *  rake scrubs all shallow pins at once but can't feel out tall stacks. */
    rakeMaxPinHeight: 3,
    /** Skill: chance a PICK shimmy at the correct pin height actually seats it
     *  (CLICK). <1 so even a perfect lift may need a couple of tries. */
    pickSkill: 0.7,
    /** Chance a PICK shimmy at the WRONG height binds a false set (CLACK); the
     *  column must be re-seated at its true height (or all pins reset). */
    falseSetChance: 0.14,
    /** Per-column chance an unset column seats on one RAKE pass. */
    rakeSetChancePerPass: 0.45,
  },

  /** Procedural dungeon generation (sim/dungeon/*, Phase 1+). Placeholder tuning
   *  — refined as the generator is built. `budget` is indexed by (budgetLevel-1),
   *  levels 1=$ dirt poor .. 5=$$$$$ filthy rich; each entry is the establishment's
   *  BuildProfile driving building scale + density. Ranges are [min,max] inclusive. */
  gen: {
    budget: [
      // $ dirt poor
      { floorW: [30, 40], floorH: [22, 30], floors: [1, 2], basements: [0, 1], windowSpacing: 7, lightSpacing: 12, guardCoverage: 0.15, lockCeil: 1, safes: [0, 1], roomLuxury: 0.0 },
      // $$ struggling
      { floorW: [42, 54], floorH: [30, 38], floors: [2, 3], basements: [1, 1], windowSpacing: 6, lightSpacing: 10, guardCoverage: 0.35, lockCeil: 2, safes: [1, 2], roomLuxury: 0.25 },
      // $$$ getting by
      { floorW: [56, 68], floorH: [38, 48], floors: [3, 4], basements: [1, 2], windowSpacing: 5, lightSpacing: 8, guardCoverage: 0.55, lockCeil: 3, safes: [2, 3], roomLuxury: 0.5 },
      // $$$$ loaded
      { floorW: [70, 84], floorH: [46, 58], floors: [4, 6], basements: [2, 2], windowSpacing: 4, lightSpacing: 7, guardCoverage: 0.7, lockCeil: 4, safes: [3, 5], roomLuxury: 0.75 },
      // $$$$$ filthy rich
      { floorW: [86, 104], floorH: [56, 70], floors: [6, 10], basements: [2, 3], windowSpacing: 3, lightSpacing: 6, guardCoverage: 0.85, lockCeil: 5, safes: [5, 8], roomLuxury: 1.0 },
    ],
    /* `guardCoverage` above: the FRACTION of each floor's patrollable area that
     * has a guard posted to it. A dirt-poor office runs one body per floor and
     * huge blind spots; a filthy-rich one watches almost everything. (It
     * replaces `guardSectionMult`, which sat in every budget row and was read
     * by nothing — every building got identical staffing.) */
    /** Recursive BSP subdivision. */
    bsp: {
      minRoom: 8,        // smallest room side (interior tiles) — stop splitting below this
      maxRoom: 26,       // keep splitting leaves wider than this so rooms aren't cavernous
      splitJitter: 0.35, // 0..0.5 — how far off-center a split may land
    },
    corridorWidth: 2,    // thin circulation "rooms" that other rooms door onto
    /** Share of desks/cabinets/shelves that hold something worth taking. Low on
     *  purpose: searching should be a gamble, not a chore. */
    searchableFurniture: 0.18,
    /** A desk drawer pays a fraction of what a safe does. */
    furnitureValueMult: 0.25,
  },

  /** Device "channel" interaction — hold to operate a device/service (hack,
   *  sabotage, plant). Interrupted by moving or dying; resets on release. */
  channel: {
    durationSec: 2.5,
  },

  /** Prop-sandbox actions (sim/dungeon props). Loudness feeds the sound flood, so
   *  every flourish carries risk. Flipping a desk = a big crash between running (9)
   *  and a silenced pistol (6)/SMG (25). */
  prop: {
    flipLoudness: 12,
    /** Climbing into a cabinet: about as loud as a crouched footstep (2) — you
     *  can hide while a guard is a room away, but not right under their nose. */
    hideLoudness: 3,
    /** Pulling a loaded shelving unit over — the loudest thing in the sandbox,
     *  louder than a silenced pistol and not far off an SMG burst. */
    toppleLoudness: 20,
    /** Sliding a desk along the floor: a scrape, not a crash. */
    shoveLoudness: 6,
    /** Heaving a body into a cabinet: heavier work than climbing in yourself. */
    stashLoudness: 5,
    /** Pocketing loose clutter — near-silent, like a knife takedown (2). */
    takeLoudness: 1,
    /** The clatter where a thrown object LANDS: a walking footstep's worth (5)
     *  plus a bit, so it reliably buys an investigation without reading as a
     *  gunshot. Emitted at the landing tile, never at the thrower. */
    throwLoudness: 7,
    /** How far a thrown object flies, in tiles. */
    throwRange: 7,
    /** How many loose objects the player can carry at once. */
    maxThrowables: 3,
  },

  /**
   * Civilians — the working population (sim/civilian.ts). Headcount is per
   * interior floor tile so a big open-plan room holds more people than a private
   * office, capped per room so no single space turns into a crowd. Routines are
   * DATA: a loop of stops, each a room kind to be in and how long to stay there.
   */
  civilian: {
    /** People per interior floor tile of a staffed room, before density. A
     *  staffed room always gets at least one person by day — an office with
     *  nobody in it isn't an office. */
    perFloorTile: 0.012,
    /** Hard cap per room, so an open-plan floor doesn't become a mob. */
    maxPerRoom: 4,
    /** Chebyshev tiles around the player's entry point kept clear of staff, so
     *  the mission doesn't open with someone standing on you. */
    spawnClearance: 3,
    /** Ticks to reach a routine stop before giving up and moving on (a blocked
     *  break room shouldn't freeze someone's whole day). */
    reachTimeoutTicks: 600,
    /** How far a civilian notices things, in tiles (omnidirectional, like the
     *  janitor — office workers aren't scanning cones). */
    sightRange: 9,
    /** A scream, in sound-flood loudness. Between a run footstep (9) and an SMG
     *  burst (25): it carries through a floor, so being seen by a worker costs
     *  you even when there's no guard in the room. */
    screamLoudness: 18,
    /** Seconds spent cowering after reporting (or with nobody left to report
     *  to) before they dare go back to work. */
    cowerSec: 25,
    /** Role routines. 'desk' = their own workstation; anything else is a room
     *  kind they walk to. Dwell times are seconds spent once they arrive. */
    routines: {
      worker: [
        { where: 'desk', dwellSec: 40 },
        { where: 'break', dwellSec: 18 },
        { where: 'desk', dwellSec: 55 },
        { where: 'restroom', dwellSec: 12 },
      ],
      manager: [
        { where: 'desk', dwellSec: 50 },
        { where: 'meeting', dwellSec: 35 },
        { where: 'desk', dwellSec: 45 },
        { where: 'break', dwellSec: 15 },
      ],
      receptionist: [
        { where: 'desk', dwellSec: 90 },
        { where: 'restroom', dwellSec: 12 },
      ],
      it: [
        { where: 'desk', dwellSec: 35 },
        { where: 'server', dwellSec: 30 },
        { where: 'break', dwellSec: 15 },
      ],
      clerk: [
        { where: 'desk', dwellSec: 30 },
        { where: 'records', dwellSec: 25 },
        { where: 'storage', dwellSec: 20 },
        { where: 'break', dwellSec: 15 },
      ],
    } as Record<string, ReadonlyArray<{ where: string; dwellSec: number }>>,
  },

  /**
   * Disguise (sim/disguise.ts). Numbers are EXPOSURE contributions, 0..1, summed
   * and clamped: 0 reads as staff, 1 is made. `base` is what the uniform costs
   * you even when behaving perfectly — a stranger is still a stranger.
   */
  disguise: {
    kinds: {
      // Security walks everywhere by right; that's the point of the uniform.
      guard: { base: 0.1, restricted: [] as string[] },
      // The janitor has keys to everything, but nobody expects them in the
      // server room or watching the cameras.
      janitor: { base: 0.15, restricted: ['security', 'server'] as string[] },
      // Office staff belong upstairs, not in plant rooms or security.
      worker: {
        base: 0.2,
        restricted: ['security', 'server', 'utility', 'boiler', 'hvac', 'pump', 'fire', 'elevator'] as string[],
      },
    },
    /** Being somewhere your uniform doesn't belong. The big one. */
    restrictedPenalty: 0.55,
    /** Sprinting down a corridor in office clothes. */
    runPenalty: 0.35,
    /** Creeping about crouched reads worse than walking, better than running. */
    crouchPenalty: 0.25,
    /** Carrying a drawn firearm (a knife doesn't count). */
    weaponPenalty: 0.4,
    /** Hugging a wall in cover — nobody does that on the way to a meeting. */
    coverPenalty: 0.3,
    /** Chance an eligible room's locker actually has a uniform in it, so a
     *  disguise is findable by exploring and not guaranteed. */
    lockerChance: 0.7,
    /** How much wrongness a CIVILIAN tolerates before reacting. They're not
     *  security: a uniform and a normal walk is enough for them, and they only
     *  raise the alarm once you're plainly out of place. */
    civilianTolerance: 0.5,
    /** Chebyshev tiles at which they see a face they don't know and the
     *  disguise stops working entirely. A uniform buys distance, not immunity. */
    madeRadius: 3,
  },

  /**
   * End-of-run payout (sim/score.ts). The bonuses are the game's opinion of how
   * a job should be done: being unseen pays more than being merciful, and both
   * pay more than the contract itself.
   */
  score: {
    contract: 2000,
    ghost: 5000,     // nobody ever saw you
    pacifist: 3000,  // nobody died
    client: 2500,    // the extraction target walked out with you
  },

  /**
   * Steam from a sabotaged boiler (sim/steam.ts). A sight-blocker that works on
   * everyone equally — including you, which is what keeps it a trade.
   */
  steam: {
    /** Cap per tile. */
    max: 5,
    /** Vented per second at the plant and each riser tap. The field settles
     *  where emission balances decay (total ≈ emitPerSec / decayRate), so this
     *  is really "how big a room one vent can fill" — ~80 tile-units. */
    emitPerSec: 25,
    /** How fast it billows into neighbouring open tiles. Diffusion is slow per
     *  tick, so this has to be well above the decay rate or the cloud stalls a
     *  few tiles from the vent instead of filling the room. */
    spreadRate: 30,
    /** How fast it thins once the source stops — a room should clear in tens of
     *  seconds, not minutes, or a single sabotage fogs the floor for the run. */
    decayRate: 0.3,
    /** Level at which a tile blocks line of sight. */
    opaqueAt: 1.0,
  },

  /**
   * Pickpocketing (sim/pickpocket.ts). Everything that makes a lift harder is
   * something the player controls or can wait out — how alert the mark is,
   * whether they're facing you, how you're standing, what you're reaching for.
   * A flat skill for now; the skill SYSTEM comes after the dungeon crawl.
   */
  pickpocket: {
    /** Clean lift on an oblivious mark, standing, reaching for a wallet. */
    base: 0.7,
    /** Multiplier by the mark's awareness state. Alert is near-hopeless. */
    awareness: { unaware: 1, suspicious: 0.55, cautious: 0.3, alert: 0.12 } as Record<string, number>,
    /** They're looking your way. */
    facingMult: 0.45,
    /** Your footing: crouched is steadier, running is hopeless. */
    crouchMult: 1.15,
    runMult: 0.35,
    /** They're mid-stride. */
    movingMult: 0.7,
    /** How much of a miss goes UNNOTICED. Beyond this band they feel it. */
    failBand: 0.18,
    /** How hard each class of thing is to lift. */
    bulk: { small: 1, medium: 0.8, large: 0.45 },
    /** Seconds before a phoned-in report reaches the guards. */
    phoneCallSec: 6,
    /** Noise a fumble makes at the victim's tile. */
    fumbleLoudness: 4,
  },

  /** Focus target selection (sim/focus.ts). Score = threat / (1 + dist × w), so
   *  `distanceWeight` is really "how far a dangerous target can be and still
   *  outrank a harmless one at your elbow". */
  focus: {
    distanceWeight: 0.35,
  },

  /** Stair travel. Standing on a stair tile starts a short hold you can walk
   *  out of, so a shove in a chase can't change your floor. */
  stairs: {
    travelSec: 0.6,
  },

  /**
   * Air-vent traversal (sim/vents.ts). A duct ignores locked doors and patrolled
   * corridors, so the cost is time and noise at both ends — and the reason to
   * sabotage the air handling is that stopped fans make it quieter and quicker.
   */
  vent: {
    /** Seconds to crawl between grilles with the fans running. */
    crawlSec: 3.5,
    /** Noise at each end of the crawl, fans running. */
    loudness: 8,
    /** Multiplier on both when the HVAC has been tampered (fans stopped). */
    fansOffMult: 0.5,
  },

  /**
   * Security cameras (sim/camera.ts). Tuned so a camera is a PUZZLE: it sweeps
   * predictably, needs light like everyone else, and takes a beat to be sure —
   * being clipped by an arc should cost position, not the run.
   */
  securityCamera: {
    /** Sight range in tiles — shorter than a guard's; it's a fixed lens. */
    range: 12,
    /** Half-angle of the cone (45° each side). */
    halfAngleRad: Math.PI / 4,
    /** How far either side of its mount the sweep swings. */
    sweepHalfAngleRad: Math.PI / 3,
    /** Sweep speed (radians of phase per second) — slow enough to time. */
    sweepRateRad: 0.6,
    /** Seconds of continuous sight before it trips the alarm. */
    alarmSec: 1.2,
    /** How fast banked suspicion drains once you're out of frame, per second. */
    forgetRate: 1.5,
    /** Cameras per floor by the building's SECURITY tier (BuildProfile.lockCeil,
     *  1..5). A dirt-poor office has none at all — a real difference in how the
     *  floor plays, not just a smaller number. */
    perFloorBySecurity: [0, 1, 2, 3, 5],
  },

  /**
   * Escort / extraction (sim/escort.ts) — the person you came to get out.
   */
  escort: {
    /** Chebyshev tiles they'll let open up before closing the gap. Big enough
     *  not to shove you in a doorway, small enough to still count as together. */
    followGap: 2,
    /** How close they must be at the exit for the extraction to count. */
    exfilRadius: 3,
  },

  /** Building operating state (time-of-day). Keyed by OperatingState. The
   *  generator's populate step reads this × budget to set initial conditions;
   *  most of it reuses existing systems (sunlight, breakers, NV, flashlights). */
  operating: {
    // In use — daytime, business hours: lit, sunlit, staffed, populated.
    'in-use': {
      hour: 13,             // feeds sunlightIntensity(hour)
      breakersOn: 1.0,      // fraction of room breakers powered
      civilianDensity: 1.0, // multiplier on civilian headcount
      guardCountMult: 1.0,
      guardNightVision: false,
      guardFlashlight: false,
      lockDensity: 0.35,    // fraction of eligible interior doors locked
    },
    // Off duty — night, after hours: dark, skeleton NV/flashlight security.
    'off-duty': {
      hour: 2,
      breakersOn: 0.15,
      civilianDensity: 0.0,
      guardCountMult: 0.6,
      guardNightVision: true,
      guardFlashlight: true,
      lockDensity: 0.8,
    },
  },
} as const;

export type Config = typeof CONFIG;
